import React from 'react';
import { Box, Stack, Typography, IconButton, Tooltip, Chip } from '@mui/material';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { getMediaUrl } from '../../utils/mediaUrl';
import { useInstatDesign } from './theme/instatDesign';

const MAX_VISIBLE_TAGS = 3;

function formatBytes(bytes) {
  if (!bytes) return '';
  const mb = bytes / (1024 * 1024);
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  return `${(bytes / 1024).toFixed(0)} KB`;
}

function formatDuration(sec) {
  if (sec == null) return '';
  const s = Math.max(0, Math.round(sec));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${String(r).padStart(2, '0')}`;
}

// Card used by ClipsTab and InformesTab. Width comes from the parent's grid
// or horizontal scroller — keep this purely presentational.
export default function ClipCard({ clip, onPlay, onDelete, t, width }) {
  const d = useInstatDesign();
  return (
    <Box
      sx={{
        flex: '0 0 auto',
        width: width || { xs: 220, sm: 240, md: 260 },
        scrollSnapAlign: 'start',
        borderRadius: `${d.radius.md}px`,
        overflow: 'hidden',
        border: `1px solid ${d.colors.divider}`,
        bgcolor: d.colors.surface,
        transition: `transform ${d.motion.base}, box-shadow ${d.motion.base}`,
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: d.shadows.cardHover,
        },
      }}
    >
      <Box
        onClick={() => onPlay && onPlay(clip)}
        sx={{
          position: 'relative',
          cursor: 'pointer',
          background: '#000',
          aspectRatio: '16 / 9',
          '&:hover .play-overlay': { opacity: 1 },
        }}
      >
        <video
          src={getMediaUrl('instat_clips', clip.filename)}
          preload="metadata"
          muted
          style={{ width: '100%', height: '100%', display: 'block', objectFit: 'cover' }}
        />
        <Box
          className="play-overlay"
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: 'rgba(0,0,0,0.35)',
            opacity: 0.7,
            transition: `opacity ${d.motion.fast}`,
          }}
        >
          <PlayArrowRoundedIcon sx={{ fontSize: 44, color: '#fff' }} />
        </Box>
        {clip.duration != null && (
          <Box
            sx={{
              position: 'absolute',
              right: 6, bottom: 6,
              px: 0.75, py: 0.15,
              bgcolor: 'rgba(0,0,0,0.65)',
              color: '#fff',
              borderRadius: 0.75,
              fontSize: 11,
              fontWeight: 600,
              ...d.typo.tabular,
            }}
          >
            {formatDuration(clip.duration)}
          </Box>
        )}
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography noWrap sx={{ fontSize: 12.5, fontWeight: 600 }}>
          {clip.match_title || clip.fname}
        </Typography>
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={0.5}>
          <Typography noWrap sx={{ ...d.typo.caption, flex: 1, minWidth: 0 }}>
            {[clip.title, clip.quality && `${clip.quality}p`, formatBytes(clip.file_size)]
              .filter(Boolean).join(' · ')}
          </Typography>
          {onDelete && (
            <Tooltip title={t ? t('instat.clips.delete') : 'Delete'}>
              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onDelete(clip); }}>
                <DeleteOutlineRoundedIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          )}
        </Stack>
        {Array.isArray(clip.tags) && clip.tags.length > 0 && (
          <Stack
            direction="row"
            spacing={0.5}
            sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}
          >
            {clip.tags.slice(0, MAX_VISIBLE_TAGS).map((tag) => (
              <Chip
                key={tag.id}
                label={tag.libelle}
                size="small"
                sx={{
                  height: 18,
                  fontSize: 10,
                  fontWeight: 600,
                  bgcolor: tag.code_couleur,
                  color: '#fff',
                  border: 'none',
                  '& .MuiChip-label': { px: 0.75 },
                }}
              />
            ))}
            {clip.tags.length > MAX_VISIBLE_TAGS && (
              <Tooltip
                title={clip.tags.slice(MAX_VISIBLE_TAGS).map((t) => t.libelle).join(', ')}
              >
                <Chip
                  label={`+${clip.tags.length - MAX_VISIBLE_TAGS}`}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: 10,
                    fontWeight: 600,
                    bgcolor: d.colors.surfaceAlt,
                    color: d.colors.textSecondary,
                    border: `1px solid ${d.colors.divider}`,
                    '& .MuiChip-label': { px: 0.75 },
                  }}
                />
              </Tooltip>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
